import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	FormControl,
	Grid,
	IconButton,
	Select,
	Table,
	TableBody,
	MenuItem,
	TableContainer,
	TableHead,
	TableSortLabel,
	TextField,
	Paper,
	checkboxClasses,
	Checkbox,
	Stack,
	Box,
	TableRow,
	styled,
} from '@mui/material';

import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as TableSortIcon } from 'common/assets/images/./table-sort-icon.svg';

import { ReactComponent as SortAscendingIcon } from 'common/assets/images/sort-asc-icon.svg';
import { ReactComponent as SortDescendingIcon } from 'common/assets/images/sort-desc-icon.svg';
import { RootState } from 'common/store/AppStore';
import { setTablePaginationHolder } from 'common/store/reducer/commonReducer';

import { nextPaginationIcon, prevPaginationIcon } from '../../assets/images';
import { TABLE_CONSTANTS } from '../../constants';
import {
	getComparator,
	ImgTagComponent,
	sortIcon,
	toSentenceCase,
	stableSort,
} from '../../utils/utilFunctions';

import {
	Column,
	Order,
	ICommonTableProps,
	StyledTableCell,
	tableThemes,
	onPageTextChanged,
} from './CommonTableStyles';

import styles from './CommonTable.module.scss';

const defaultPagination = {
	pageNumber: 0,
	pageSize: TABLE_CONSTANTS.customPageSize,
};

const CommonTable = (props: ICommonTableProps) => {
	const {
		id,
		rows,
		columns,
		defaultSortKey,
		tableStyles,
		isCheckBoxEnabled,
		onRowSelectable,
		onRowChecked,
		enableHeaderColor,
		hidePagination,
		enableInnerTableRow,
		customStylesEnabled,
	} = props;

	const dispatch = useDispatch();

	const storePagination = useSelector((appState: RootState) => {
		if (id) {
			return appState.common.tablePaginationHolder[id] || defaultPagination;
		}
		return defaultPagination;
	});

	const { checkboxSelectionColor, sortIconColor } = styles;
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<keyof any>(defaultSortKey || '');

	const [page, setPage] = React.useState(storePagination.pageNumber);
	const [rowsPerPage, setRowsPerPage] = React.useState(
		storePagination.pageSize,
	);

	const [textFieldPage, setTextFieldPage] = React.useState<any>(
		storePagination.pageNumber + 1,
	);
	const [selectedIds, setSelectedIds] = React.useState<any>([]);

	const minWidth = '32.625rem';

	const isSelected: any = (id: any) => selectedIds.indexOf(id) !== -1;

	const tableHeadColumColor = enableHeaderColor
		? tableThemes[0].headColor
		: tableThemes[1].headColor;
	const TableRowStyled = styled(TableRow)`	
  		&:nth-of-type(odd) {
    		 background-color:  ${
						enableHeaderColor
							? tableThemes[0].bodyColor
							: tableThemes[1].bodyColor
					};
			 border-radius:'4px';
  		},	
	`;

	const TableHeadStyled = styled(TableHead)`
		& .MuiTableCell-root {
			background-color: ${tableHeadColumColor} !important;
			height: var(--spacing-32-px, 32px);
			flex-shrink: 0;
		}
	`;
	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof any,
	) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const visibleRows = React.useMemo(
		() =>
			stableSort(rows, getComparator(order, orderBy)).slice(
				page * rowsPerPage,
				page * rowsPerPage + rowsPerPage,
			),
		[order, orderBy, page, rowsPerPage, rows],
	);
	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
		setTextFieldPage(1);
	};
	const handlePrevPagination = (cPage: number) => {
		setTextFieldPage(cPage - 1);
		setPage(page - 1);
	};
	const handleNextPagination = (cPage: number) => {
		setPage(cPage);
		setTextFieldPage(cPage + 1);
	};

	const onPageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(event?.target?.value, 10);
		const resultValue = onPageTextChanged(value, rowsPerPage, rows?.length);
		setPage(resultValue - 1);
		setTextFieldPage(resultValue);
	};

	const handleShowEndPage = () => {
		if (rows.length > 0) {
			const val = rows.length / rowsPerPage;
			if (val % 1 !== 0) {
				return Math.trunc(val + 1);
			}
			return rows.length / rowsPerPage;
		}
		return rows.length / rowsPerPage;
	};
	const onCheckBoxSelected = (event: any, data: any) => {
		event.stopPropagation();
		const selectedIndex = selectedIds.indexOf(data.id);
		let newSelected: readonly any[] = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selectedIds, data.id);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selectedIds.slice(1));
		} else if (selectedIndex === selectedIds.length - 1) {
			newSelected = newSelected.concat(selectedIds.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selectedIds.slice(0, selectedIndex),
				selectedIds.slice(selectedIndex + 1),
			);
		}

		setSelectedIds(newSelected);
		if (onRowChecked) {
			onRowChecked(newSelected);
		}
	};
	const onCheckBoxAllSelected = (
		event: React.ChangeEvent<HTMLInputElement>,
		data: any,
	) => {
		if (event.target.checked) {
			const tmpIds: string[] = [];
			data.forEach((ele: any) => {
				tmpIds.push(ele?.id);
			});
			setSelectedIds(tmpIds);
		} else {
			setSelectedIds([]);
		}
		if (onRowChecked) {
			onRowChecked(selectedIds);
		}
	};

	const onTableRowSelect = (event: any, data: any) => {
		if (!event?.target?.classList?.contains('selectCheckbox')) {
			if (onRowSelectable) {
				onRowSelectable(data);
			}
		}
	};

	const createSortHandler =
		(property: keyof any) => (event: React.MouseEvent<unknown>) => {
			handleRequestSort(event, property);
		};
	useEffect(() => {
		setSelectedIds([]);
		if (hidePagination) {
			setRowsPerPage(rows?.length);
		}
	}, [rows.length]);

	useEffect(() => {
		if (!id) return;
		dispatch(
			setTablePaginationHolder({
				[id]: {
					pageNumber: page,
					pageSize: rowsPerPage,
				},
			}),
		);
	}, [page, rowsPerPage]);

	const getMaxHeight = () => {
		if (customStylesEnabled) {
			const tableHeight = tableStyles?.maxHeight;
			return tableHeight;
		}
		return rows.length * 40 * 40;
	};

	const getOverFlow = () => {
		let overflow = 'auto';
		if (customStylesEnabled) {
			overflow = 'scroll !important';
			return overflow;
		}
		return overflow;
	};

	return (
		<div className={styles['common-table']} data-testid="main-div">
			<Paper
				sx={{ width: '100%' }}
				className={
					enableInnerTableRow
						? styles['table-box-shadow-none']
						: styles['table-box-shadow']
				}
			>
				<TableContainer
					className={styles['common-table-container']}
					sx={{
						maxHeight: getMaxHeight(),
						overflowY: getOverFlow(),
						overflow: getOverFlow(),
					}}
				>
					<Table
						aria-labelledby="tableTitle"
						stickyHeader
						sx={{
							minWidth: tableStyles?.minWidth
								? tableStyles?.minWidth
								: minWidth,
							paddingTop: '8px',
							paddingLeft: enableInnerTableRow ? '0px' : '16px',
							paddingRight: enableInnerTableRow ? '0px' : '15px',
							overflowY: 'auto',
						}}
					>
						<TableHeadStyled>
							<TableRowStyled>
								{isCheckBoxEnabled && rows.length > 0 && (
									<StyledTableCell
										component="th"
										scope="row"
										key="enhanced-table-colheader-checkbox"
										align="left"
										className={styles['table-column-cell-head']}
									>
										<Checkbox
											size="small"
											aria-checked={
												rows.length > 0 && selectedIds.length === rows.length
											}
											data-testid="checkbox-all-selection"
											name="CheckBoxOutlineBlankIcon"
											role="checkbox"
											sx={{
												'& :hover': { background: 'none' },
												[`&, &.${checkboxClasses.checked}`]: {
													color: checkboxSelectionColor,
												},
											}}
											checked={
												rows.length > 0 && selectedIds.length === rows.length
											}
											onChange={(e) => {
												onCheckBoxAllSelected(e, rows);
											}}
										/>
									</StyledTableCell>
								)}
								{columns &&
									columns.map((headCell: Column) => {
										const colId = `enhanced-table-colheader-${headCell?.id}`;
										return (
											<StyledTableCell
												component="th"
												scope="row"
												key={colId}
												align="left"
												sortDirection={orderBy === headCell?.id ? order : false}
												className={styles['table-column-cell-head']}
											>
												<TableSortLabel
													data-testid="table-sort"
													active={
														headCell?.isSortable || orderBy === headCell?.id
													}
													direction={orderBy === headCell?.id ? order : 'asc'}
													onClick={
														headCell?.isSortable
															? createSortHandler(headCell?.id)
															: undefined
													}
													className={styles['table-column-cell-head']}
													IconComponent={sortIcon}
													hideSortIcon={!headCell?.isSortable}
													sx={{
														'&:hover': {
															cursor: !headCell?.isSortable
																? 'default'
																: 'pointer',
														},
														'& .MuiIcon-root': {
															display: 'none',
														},
													}}
												>
													<Stack direction="row" spacing={1}>
														<Box
															component="span"
															className={styles['table-column-cell-col-label']}
														>
															{headCell?.hideSentenceCase
																? headCell?.label
																: toSentenceCase(headCell?.label)}{' '}
														</Box>

														{orderBy === headCell.id ? (
															<Box component="span">
																{order === 'desc' ? (
																	<SortDescendingIcon fill={sortIconColor} />
																) : (
																	<SortAscendingIcon fill={sortIconColor} />
																)}
															</Box>
														) : (
															<Box component="span">
																{' '}
																{headCell?.isSortable ? (
																	<TableSortIcon fill={sortIconColor} />
																) : null}
															</Box>
														)}
													</Stack>
												</TableSortLabel>
											</StyledTableCell>
										);
									})}
							</TableRowStyled>
						</TableHeadStyled>
						<TableBody data-testid="table-data">
							{visibleRows.map((data: any) => {
								const isItemSelected = isSelected(data?.id);
								return (
									<TableRowStyled
										hover
										tabIndex={-1}
										key={data?.id}
										data-testid="table-rows"
										onClick={(event: any) => {
											onTableRowSelect(event, data);
										}}
									>
										{isCheckBoxEnabled && (
											<StyledTableCell
												component="th"
												scope="row"
												key="checkbox"
												align="left"
												className={styles['table-column-cell']}
											>
												<Checkbox
													size="small"
													name={data?.id}
													role="checkbox"
													aria-checked={isItemSelected}
													data-testid="checkbox-selection"
													inputProps={{ className: 'selectCheckbox' }}
													sx={{
														'& :hover': { background: 'none' },
														[`&, &.${checkboxClasses.checked}`]: {
															color: checkboxSelectionColor,
														},
													}}
													checked={isItemSelected}
													onChange={(e) => {
														onCheckBoxSelected(e, data);
													}}
												/>
											</StyledTableCell>
										)}
										{columns &&
											columns.map((v: Column) => (
												<StyledTableCell
													align="left"
													key={`enhanced-table-checkbox-${v.id}${data[v.id]}`}
													className={styles['table-column-cell']}
													sx={{
														height: 50,
													}}
												>
													{v.render ? v.render(data) : data[v.id]}
												</StyledTableCell>
											))}
									</TableRowStyled>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
			{!hidePagination && (
				<Grid container>
					<Grid item xs={6} className={styles['common-table-rows-per-page']}>
						<FormControl
							variant="standard"
							sx={{ m: 1, minWidth: 120 }}
							className={styles['common-table-form-control']}
						>
							<span className={styles['common-table-show-text']}>Show</span>
							<Select
								type="listbox"
								data-testid="pages-select"
								value={rowsPerPage}
								IconComponent={ExpandMoreRoundedIcon}
								onChange={(e: any) => handleChangeRowsPerPage(e)}
								className={styles['common-table-select']}
								id="demo-simple-select-standard"
								variant="standard"
							>
								<MenuItem value={10}>10</MenuItem>
								<MenuItem value={25}>25</MenuItem>
								<MenuItem value={50}>50</MenuItem>
							</Select>
							<span className={styles['common-table-show-text']}>items</span>
						</FormControl>
					</Grid>
					<Grid item xs={6} className={styles['common-table-pages-grid']}>
						<IconButton
							aria-label="delete"
							data-testid="prev-icon"
							onClick={() => {
								handlePrevPagination(page + 1);
							}}
							className={styles['common-table-icon-button-prev']}
							disabled={page === 0}
						>
							{ImgTagComponent(
								prevPaginationIcon,
								'prevPaginationIcon',
								'prev-pagination-styles',
							)}
						</IconButton>
						<TextField
							id="outlined-size-small"
							value={textFieldPage}
							size="small"
							className={styles['common-table-pages-grid-input-field']}
							onChange={(e: any) => onPageSelect(e)}
							inputProps={{ 'data-testid': 'pagenumber-textfield' }}
						/>
						of {handleShowEndPage()}
						<IconButton
							aria-label="delete"
							data-testid="next-icon"
							onClick={() => {
								handleNextPagination(page + 1);
							}}
							className={styles['common-table-icon-button-next']}
							disabled={page + 1 === handleShowEndPage()}
						>
							{ImgTagComponent(
								nextPaginationIcon,
								'nextPaginationIcon',
								'next-pagination-styles',
							)}
						</IconButton>
					</Grid>
				</Grid>
			)}
		</div>
	);
};

export default CommonTable;
